import React, { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { Spinner, ShieldCheck } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Native PayPal / Card / Venmo / Apple Pay / Google Pay buttons.
 *
 * Props:
 *  - createOrder: async () => order_id (call your backend to create a PayPal order)
 *  - onApproved: (session_id) => void  (redirect / success handler)
 *  - onCancel:   optional () => void
 *  - disabled:   optional boolean — greys out the buttons
 *  - amount:     optional number — only used for display ("Pay $X.XX")
 *
 * Apple Pay + Google Pay only render on devices/browsers that support them
 * AND when the merchant has enabled the funding source in the PayPal dashboard
 * (with Apple Pay domain association file for Apple Pay).
 */
export default function PayPalWalletButtons({ createOrder, onApproved, onCancel, disabled, amount }) {
  const [cfg, setCfg] = useState(null);
  const [cfgErr, setCfgErr] = useState(null);
  const [capturing, setCapturing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/payments/paypal/config");
        if (!cancelled) {
          if (!data?.enabled || !data?.client_id) setCfgErr("PayPal is not configured.");
          else setCfg(data);
        }
      } catch (e) {
        if (!cancelled) setCfgErr("Could not load PayPal.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (cfgErr) {
    return <p data-testid="paypal-wallet-error" className="text-sm text-red-600">{cfgErr}</p>;
  }
  if (!cfg) {
    return (
      <div data-testid="paypal-wallet-loading" className="inline-flex items-center gap-2 text-sm text-[var(--ji-muted)]">
        <Spinner size={16} className="animate-spin" /> Loading secure checkout…
      </div>
    );
  }

  const scriptOptions = {
    "client-id": cfg.client_id,
    currency: "USD",
    intent: "capture",
    // Ask the SDK to render every wallet the buyer's device is eligible for.
    "enable-funding": "venmo,paylater",
    components: "buttons",
    "disable-funding": "credit",
  };

  return (
    <div data-testid="paypal-wallet-buttons" className={disabled ? "pointer-events-none opacity-60" : ""}>
      <PayPalScriptProvider options={scriptOptions}>
        <PayPalButtons
          style={{ layout: "vertical", shape: "rect", label: "pay", height: 48, tagline: false }}
          disabled={!!disabled || capturing}
          fundingSource={undefined}
          forceReRender={[amount, disabled]}
          createOrder={async () => {
            try {
              const orderId = await createOrder();
              if (!orderId) throw new Error("No order id returned");
              return orderId;
            } catch (e) {
              toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not start checkout");
              throw e;
            }
          }}
          onApprove={async (data) => {
            setCapturing(true);
            try {
              // Backend status endpoint auto-captures APPROVED PayPal orders and
              // returns the internal session_id.
              const { data: sq } = await api.get(`/payments/status/${data.orderID}`);
              if (sq?.status === "paid") {
                onApproved(sq.session_id || data.orderID);
              } else {
                toast.error("Payment did not complete. Please try again.");
              }
            } catch (e) {
              toast.error("Could not confirm payment. Please refresh and check your email.");
            } finally {
              setCapturing(false);
            }
          }}
          onCancel={() => { if (onCancel) onCancel(); toast.info("Checkout cancelled."); }}
          onError={(err) => {
            console.error("PayPal error", err);
            toast.error("PayPal reported an error. Please try another payment method.");
          }}
        />
      </PayPalScriptProvider>
      <p className="mt-3 text-xs text-[var(--ji-muted)] inline-flex items-center gap-2">
        <ShieldCheck size={14} weight="bold" className="text-[var(--ji-accent)]" />
        PayPal, credit/debit card{cfg.env === "live" ? "" : " (sandbox)"}, Venmo, Apple Pay & Google Pay supported where available.
      </p>
    </div>
  );
}
